import type { HTMLAttributes } from 'react';
import { cn } from '../lib/cn';
import { avatarColour } from '../tokens';

export interface AvatarProps extends HTMLAttributes<HTMLSpanElement> {
  /** Two-letter initials, as every avatar in the export shows. */
  initials: string;
  /** Diameter in px. The export uses 26 · 32 · 34 · 38 · 40 · 42 · 46 · 60 · 72. */
  size?: number;
  /**
   * Explicit fill. Omit to derive one deterministically from `colourKey`
   * (falling back to the initials) so a person keeps their colour everywhere.
   */
  colour?: string;
  colourKey?: string;
  /** Past trips render their avatar slightly knocked back. */
  faded?: boolean;
  /** Accessible name — usually the member's full name. */
  name?: string;
}

/** Font sizes the export pairs with each diameter. */
const FONT_FOR_SIZE: Record<number, number> = {
  26: 10,
  32: 12,
  34: 12,
  38: 13,
  40: 13,
  42: 14,
  46: 15,
  60: 20,
  72: 26,
};

export function Avatar({
  initials,
  size = 40,
  colour,
  colourKey,
  faded = false,
  name,
  className,
  style,
  ...rest
}: AvatarProps) {
  const fill = colour ?? avatarColour(colourKey ?? initials);
  const fontSize = FONT_FOR_SIZE[size] ?? Math.round(size * 0.33);

  return (
    <span
      role="img"
      aria-label={name ?? initials}
      className={cn(
        'inline-flex flex-none items-center justify-center rounded-full font-bold text-white',
        faded && 'opacity-80',
        className,
      )}
      style={{
        width: size,
        height: size,
        fontSize,
        background: fill,
        ...style,
      }}
      {...rest}
    >
      {initials}
    </span>
  );
}
